import React, { useState } from 'react';
import {
  ActionIcon,
  AvailableIcons,
  Button,
  DropDown,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { RankingStrategyTypes } from 'src/services';
import { orderOptions } from 'src/components-bl/RankingStrategy/RankingStrategyTable/rankingDefaults';
import { AddItemCard } from '../AddItemCard';
import { ControlSnapshotPanel } from '../ControlSnapshotPanel';
import { BlastRadiusBanner } from '../BlastRadiusBanner';
import { FullConfigDiffModal } from '../FullConfigDiffModal';
import { RankingStrategyEditorProps } from './types';
import { useRankingStrategyEditor } from './useRankingStrategyEditor';
import './RankingStrategyEditor.scss';

const MIN_WEIGHT = 0;
const MAX_WEIGHT = 100;

export const RankingStrategyEditor = (props: RankingStrategyEditorProps): JSX.Element => {
  const {
    isLoading,
    hasError,
    rows,
    controlItems,
    changeCount,
    surfaceLabel,
    addableFields,
    onWeightChange,
    onAddParameter,
    onRemoveParameter,
  } = useRankingStrategyEditor(props);
  const { testVariant } = props;
  const [isFullDiffOpen, setIsFullDiffOpen] = useState(false);
  const changedEntities = testVariant?.versionChangesSummary?.changedEntities;

  const [isAddingParameter, setIsAddingParameter] = useState(false);
  const [newName, setNewName] = useState('');
  const [newField, setNewField] = useState('');
  const [newOrder, setNewOrder] = useState(RankingStrategyTypes.RankingOrder.Desc);
  const [newWeight, setNewWeight] = useState(0);

  const closeComposer = (): void => {
    setIsAddingParameter(false);
    setNewName('');
    setNewField('');
    setNewOrder(RankingStrategyTypes.RankingOrder.Desc);
    setNewWeight(0);
  };

  const selectedFieldOption = addableFields.find(field => field.value === newField);
  const newParameterName = newName || selectedFieldOption?.text || '';
  const orderLabel = orderOptions.find(option => option.value === newOrder)?.text ?? '';

  const handleApplyNewParameter = (): void => {
    if (!newField) return;
    onAddParameter({
      field: newField,
      name: newParameterName || newField,
      order: newOrder,
      weight: newWeight,
    });
    closeComposer();
  };

  if (isLoading) {
    return <div className='ranking-strategy-table-loader'>Loading...</div>;
  }

  if (hasError) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Couldn&apos;t load ranking weights for this surface. This can happen if the catalogue has no
        items yet — add items in Catalogue Manager, then come back to this step.
      </Typography>
    );
  }

  return (
    <div className='syte-ranking-strategy-editor'>
      <ControlSnapshotPanel items={controlItems} />

      <div className='syte-ranking-strategy-editor-variant'>
        <div className='syte-ranking-strategy-editor-variant-header'>
          <span className='syte-ranking-strategy-editor-variant-dot' />
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            {`Variant B · Ranking strategy on ${surfaceLabel}`}
          </Typography>
          <span className='syte-ranking-strategy-editor-variant-count'>
            {`${changeCount} change${changeCount === 1 ? '' : 's'}`}
          </span>
        </div>

        <table className='syte-ranking-strategy-editor-table'>
          <thead>
            <tr>
              <th>Ranking parameter</th>
              <th>Control</th>
              <th>Variant</th>
              <th>Change</th>
              <th aria-hidden className='syte-ranking-strategy-editor-table-remove-header' />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.index}>
                <td>
                  <div className='syte-ranking-strategy-editor-table-name'>{row.name}</div>
                  <div className='syte-ranking-strategy-editor-table-source'>{row.sourceLabel}</div>
                </td>
                <td className='syte-ranking-strategy-editor-table-control'>
                  {row.controlWeight ?? '—'}
                </td>
                <td>
                  <div className='syte-ranking-strategy-editor-table-slider'>
                    <input
                      type='range'
                      min={MIN_WEIGHT}
                      max={MAX_WEIGHT}
                      value={row.variantWeight}
                      onChange={event => onWeightChange(row.index, Number(event.target.value))}
                    />
                    <input
                      type='number'
                      min={MIN_WEIGHT}
                      max={MAX_WEIGHT}
                      value={row.variantWeight}
                      onChange={event =>
                        onWeightChange(
                          row.index,
                          Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, Number(event.target.value)))
                        )
                      }
                    />
                  </div>
                </td>
                <td className='syte-ranking-strategy-editor-table-change'>
                  {row.delta === undefined || row.delta === 0
                    ? '—'
                    : row.delta > 0
                      ? `+${row.delta}`
                      : row.delta}
                </td>
                <td className='syte-ranking-strategy-editor-table-remove'>
                  {/* Syte Core ranking fields are permanent, same as the existing (non-wizard)
                  ranking drawer - only real data-field parameters (pre-existing or just added)
                  can be removed. */}
                  {!row.isSyteCore && (
                    <ActionIcon
                      prefixId={`variantsStep_rankingStrategy_removeParameter_${row.index}`}
                      iconName={AvailableIcons.TrashCan}
                      size='small'
                      onClick={() => onRemoveParameter(row.index)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {isAddingParameter && (
          <AddItemCard
            title='New ranking parameter'
            onApply={handleApplyNewParameter}
            onCancel={closeComposer}
            isApplyDisabled={!newField}
            summary={
              newField
                ? `Rank items by ${newParameterName}, ${orderLabel.toLowerCase()}, weight ${newWeight}.`
                : 'Pick a data field to see how this parameter will rank items.'
            }
          >
            <input
              type='text'
              placeholder='Name'
              className='syte-ranking-strategy-editor-add-row-name'
              value={newName}
              onChange={event => setNewName(event.target.value)}
            />
            <DropDown
              placeholder='Data field'
              options={addableFields}
              value={newField || undefined}
              onChange={value => setNewField(String(value))}
              className='syte-ranking-strategy-editor-add-row-field'
            />
            <DropDown
              placeholder='Order'
              options={orderOptions}
              value={newOrder}
              onChange={value => setNewOrder(value as RankingStrategyTypes.RankingOrder)}
              className='syte-ranking-strategy-editor-add-row-order'
            />
            <div className='syte-ranking-strategy-editor-add-row-weight'>
              <input
                type='range'
                min={MIN_WEIGHT}
                max={MAX_WEIGHT}
                value={newWeight}
                onChange={event => setNewWeight(Number(event.target.value))}
              />
              <input
                type='number'
                min={MIN_WEIGHT}
                max={MAX_WEIGHT}
                value={newWeight}
                onChange={event =>
                  setNewWeight(
                    Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, Number(event.target.value)))
                  )
                }
              />
            </div>
          </AddItemCard>
        )}

        <div className='syte-ranking-strategy-editor-actions'>
          <Button
            prefixId='variantsStep_rankingStrategy_addChange'
            variant='secondary'
            startIcon={AvailableIcons.Add}
            onClick={() => setIsAddingParameter(true)}
            disabled={isAddingParameter}
          >
            Add another change
          </Button>
          <Button
            prefixId='variantsStep_rankingStrategy_fullConfigDiff'
            variant='tertiary'
            onClick={() => setIsFullDiffOpen(true)}
            disabled={!changedEntities}
          >
            Full config diff
          </Button>
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-ranking-strategy-editor-actions-caption'
          >
            Every other setting stays identical to control.
          </Typography>
        </div>

        {changeCount > 1 && (
          <BlastRadiusBanner changeCount={changeCount} scopeLabel={surfaceLabel} />
        )}

        {/* TODO: results preview (top 5 for a sample product) — implement later */}
      </div>

      {isFullDiffOpen && changedEntities && (
        <FullConfigDiffModal
          isOpen={isFullDiffOpen}
          onClose={() => setIsFullDiffOpen(false)}
          changedEntities={changedEntities}
          variantName={testVariant?.name}
        />
      )}
    </div>
  );
};
